import { isDevPreview, devPreviewUser } from '../boot/devPreview';
import { sb } from './supabase';
import { logActivity } from './repo';
import { STANDARD_BLOCKS } from '../cad/blocks/standardLibrary';
import { normalizeParams, shelfLengthMeters } from '../cad/blocks/parametric';
import type { CatalogBlock } from '../cad/blocks/catalog';
import type { CadEntity } from '../cad/model/entity';

/**
 * 블록 마스터 데이터 저장소 (작업지시서 P4-06)
 *
 * `cad_block`(global/brand/store_type 스코프)은 전 프로젝트가 공유하는 마스터다.
 * 값을 직접 update 하는 경로는 없다 — `cad_block_update` RLS(0009)가 그 스코프의
 * 직접 수정을 막아 두었다. 수정은 항상 "제안 → (다른 관리자의) 승인" 두 단계를
 * 거치며, 승인은 `cad_apply_block_edit()` RPC 를 통해서만 실제 반영된다
 * (`supabase/migrations/0009_cad_block_edit.sql` 참고).
 *
 * 패턴은 `collabRepo.ts` 의 버전 승인 흐름과 동일하다: 반려 사유 필수,
 * 낙관적 동시성(그 사이 처리됐으면 재시도 유도), 모든 처리는 활동 로그에 남긴다.
 */

function fail(where: string, error: { message: string } | null): void {
  if (error) throw new Error(`${where}: ${error.message}`);
}

export interface BlockMasterRow {
  id: string;
  blockCode: string;
  name: string;
  categoryCode: string;
  defaultLayer: string;
  widthMm: number | null;
  depthMm: number | null;
  heightMm: number | null;
  shelfLengthPerUnitM: number | null;
  clearance: { frontMm?: number; sideMm?: number; rearMm?: number };
  utility: { power?: number | null; water?: boolean; drain?: boolean; heatLoadW?: number };
  cost: { standardKrw?: number; installKrw?: number };
  status: 'draft' | 'approved' | 'deprecated';
  /** 도면에서 직접 그려 등록한 블록만 있다 — 표준 60종은 코드에 내장돼 geometry_json 이 비어 있다. */
  staticEntities: CadEntity[] | null;
  /** 삽입할 때 크기(배율)를 바꿀 수 있는지 — 도형이 있는 행에만 의미가 있다. */
  resizable: boolean;
}

/** 화면 폼이 채우는 값 — DB 컬럼명과 그대로 대응한다 (RPC 안에서 키로 꺼내 쓴다). */
export interface BlockEditChanges {
  name?: string;
  category_code?: string;
  default_layer?: string;
  width_mm?: number;
  depth_mm?: number;
  height_mm?: number;
  shelf_length_per_unit_m?: number;
  clearance?: BlockMasterRow['clearance'];
  utility?: BlockMasterRow['utility'];
  cost?: BlockMasterRow['cost'];
}

export type BlockEditStatus = 'pending' | 'approved' | 'rejected';

export interface BlockEditRequestRow {
  id: string;
  blockId: string;
  blockCode: string;
  status: BlockEditStatus;
  changes: BlockEditChanges;
  previous: Record<string, unknown> | null;
  note: string | null;
  reason: string | null;
  proposedBy: string;
  proposedAt: string;
  reviewedBy: string | null;
  reviewedAt: string | null;
}

/* -------------------------------------------------------------------------- *
 * 조회
 * -------------------------------------------------------------------------- */

export async function listBlocks(): Promise<BlockMasterRow[]> {
  if (isDevPreview()) return devBlocks();

  const client = sb();
  const { data, error } = await client
    .from('cad_block')
    .select(
      'id, block_code, name, category_code, default_layer, width_mm, depth_mm, height_mm, shelf_length_per_unit_m, clearance, utility, cost, status, geometry_json'
    )
    .eq('scope', 'global')
    .order('block_code', { ascending: true });
  fail('블록 목록 조회', error);

  return (data ?? []).map(mapBlockRow);
}

function mapBlockRow(r: Record<string, unknown>): BlockMasterRow {
  return {
    id: r.id as string,
    blockCode: r.block_code as string,
    name: r.name as string,
    categoryCode: r.category_code as string,
    defaultLayer: r.default_layer as string,
    widthMm: (r.width_mm as number | null) ?? null,
    depthMm: (r.depth_mm as number | null) ?? null,
    heightMm: (r.height_mm as number | null) ?? null,
    shelfLengthPerUnitM: (r.shelf_length_per_unit_m as number | null) ?? null,
    clearance: (r.clearance as BlockMasterRow['clearance']) ?? {},
    utility: (r.utility as BlockMasterRow['utility']) ?? {},
    cost: (r.cost as BlockMasterRow['cost']) ?? {},
    status: r.status as BlockMasterRow['status'],
    staticEntities: parseGeometryJson(r.geometry_json)?.entities ?? null,
    resizable: parseGeometryJson(r.geometry_json)?.resizable ?? false,
  };
}

interface ParsedGeometry {
  entities: CadEntity[];
  resizable: boolean;
}

/**
 * `geometry_json` 은 스키마가 고정돼 있지 않은 jsonb 다 — 도형 등록 기능이
 * 생기면서 이 저장소가 정한 모양(`{ entities, resizable }`)일 뿐이다.
 * DB 컬럼(width_mm 등)과 달리 여기 새 필드를 추가할 때 마이그레이션이
 * 필요 없다 — resizable 플래그를 여기 둔 이유.
 */
function parseGeometryJson(raw: unknown): ParsedGeometry | null {
  if (!raw || typeof raw !== 'object') return null;
  const entities = (raw as { entities?: unknown }).entities;
  if (!Array.isArray(entities) || entities.length === 0) return null;
  return {
    entities: entities as CadEntity[],
    resizable: (raw as { resizable?: unknown }).resizable === true,
  };
}

/** DB 행 → 도면에 놓을 수 있는 카탈로그 항목. 도형이 없는 행(표준 60종 그림자)은 건너뛴다. */
export function blockRowToCatalogBlock(row: BlockMasterRow): CatalogBlock | null {
  if (!row.staticEntities) return null;
  return {
    code: row.blockCode,
    name: row.name,
    categoryCode: row.categoryCode,
    staticEntities: row.staticEntities,
    defaultLayer: row.defaultLayer,
    clearance: row.clearance,
    cost: row.cost,
    utility: row.utility,
    widthMm: row.widthMm ?? undefined,
    depthMm: row.depthMm ?? undefined,
    resizable: row.resizable,
  };
}

export async function listEditRequests(status?: BlockEditStatus): Promise<BlockEditRequestRow[]> {
  if (isDevPreview()) return [...DEV_REQUESTS.values()].filter((r) => !status || r.status === status);

  const client = sb();
  let q = client.from('cad_block_edit_request').select('*').order('proposed_at', { ascending: false });
  if (status) q = q.eq('status', status);
  const { data, error } = await q;
  fail('수정 제안 조회', error);

  return (data ?? []).map(mapRequestRow);
}

function mapRequestRow(r: Record<string, unknown>): BlockEditRequestRow {
  return {
    id: r.id as string,
    blockId: r.block_id as string,
    blockCode: r.block_code as string,
    status: r.status as BlockEditStatus,
    changes: (r.changes as BlockEditChanges) ?? {},
    previous: (r.previous as Record<string, unknown> | null) ?? null,
    note: (r.note as string | null) ?? null,
    reason: (r.reason as string | null) ?? null,
    proposedBy: r.proposed_by as string,
    proposedAt: r.proposed_at as string,
    reviewedBy: (r.reviewed_by as string | null) ?? null,
    reviewedAt: (r.reviewed_at as string | null) ?? null,
  };
}

/* -------------------------------------------------------------------------- *
 * 도면에서 그린 도형 → 새 블록 등록
 * -------------------------------------------------------------------------- */

export interface NewBlockInput {
  name: string;
  categoryCode: string;
  defaultLayer: string;
  widthMm: number;
  depthMm: number;
  heightMm?: number | null;
  clearance?: BlockMasterRow['clearance'];
  utility?: BlockMasterRow['utility'];
  cost?: BlockMasterRow['cost'];
  /** 블록 좌표계(원점 = 외접 사각 좌하단)로 이미 옮겨진 도형 */
  geometry: CadEntity[];
  /** true 면 삽입할 때 폭을 새로 입력해 크기를 바꿀 수 있다 */
  resizable: boolean;
}

/** 이름에서 코드를 만든다. 사람이 봐도 뭔지 알아보게 하고, 시각으로 겹치지 않게 한다. */
function slugifyBlockCode(name: string): string {
  const base = name
    .trim()
    .replace(/[^0-9A-Za-z가-힣]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24)
    .toUpperCase();
  return `${base || 'BLOCK'}-${Date.now().toString(36).toUpperCase()}`;
}

/**
 * 도면에서 그린 도형을 새 블록으로 등록한다.
 *
 * ★ 관리자만 부를 수 있는 화면(CAD관리자·시스템관리자, SidePanel Properties)에서만
 *   호출하므로, 기존 블록 수정처럼 "제안 → 다른 관리자 승인" 단계를 두지 않고
 *   바로 등록한다 — RLS(`cad_block_insert`, 0005)도 이미 admin scope=global 직접
 *   insert 를 허용하고 있다.
 */
export async function registerBlock(input: NewBlockInput): Promise<BlockMasterRow> {
  const blockCode = slugifyBlockCode(input.name);

  if (isDevPreview()) {
    return {
      id: `dev-custom-${Date.now().toString(36)}`,
      blockCode,
      name: input.name,
      categoryCode: input.categoryCode,
      defaultLayer: input.defaultLayer,
      widthMm: input.widthMm,
      depthMm: input.depthMm,
      heightMm: input.heightMm ?? null,
      shelfLengthPerUnitM: null,
      clearance: input.clearance ?? {},
      utility: input.utility ?? {},
      cost: input.cost ?? {},
      status: 'approved',
      staticEntities: input.geometry,
      resizable: input.resizable,
    };
  }

  const client = sb();
  const { data, error } = await client
    .from('cad_block')
    .insert({
      block_code: blockCode,
      name: input.name,
      category_code: input.categoryCode,
      scope: 'global',
      default_layer: input.defaultLayer,
      width_mm: input.widthMm,
      depth_mm: input.depthMm,
      height_mm: input.heightMm ?? null,
      clearance: input.clearance ?? {},
      utility: input.utility ?? {},
      cost: input.cost ?? {},
      geometry_json: { entities: input.geometry, resizable: input.resizable },
      status: 'approved',
    })
    .select(
      'id, block_code, name, category_code, default_layer, width_mm, depth_mm, height_mm, shelf_length_per_unit_m, clearance, utility, cost, status, geometry_json'
    )
    .single();
  fail('블록 등록', error);

  await logActivity('block.create', { payload: { blockCode } });

  return mapBlockRow(data as Record<string, unknown>);
}

/**
 * 도면에서 등록한 블록을 삭제한다.
 *
 * ★ 표준 60종은 이 경로로 지우지 않는다 — 그 도형은 코드(standardLibrary.ts)에
 *   내장돼 있어 DB 행을 지워도 편집기에서는 그대로 나오고, 관리 화면 표에서만
 *   사라져 혼란스럽다. 화면(BlockAdmin.tsx)에서 도형이 있는(geometry_json 이
 *   채워진) 행에만 삭제 버튼을 보여주는 방식으로 막는다.
 *
 * ★ 이미 이 블록을 쓴 도면은 영향받지 않는다 — 도형 정의가 각 도면의
 *   `document_json.blocks` 안에 그대로 남아 있다(INSERT 시 문서에 복사해
 *   넣는 방식이라 마스터를 지워도 참조가 깨지지 않는다).
 */
export async function deleteBlock(id: string, blockCode: string): Promise<void> {
  if (isDevPreview()) throw new Error('개발 미리보기 모드에서는 삭제할 수 없습니다.');

  const client = sb();
  const { error } = await client.from('cad_block').delete().eq('id', id);
  fail('블록 삭제', error);

  await logActivity('block.delete', { payload: { blockCode } });
}

/* -------------------------------------------------------------------------- *
 * 제안 · 승인 · 반려
 * -------------------------------------------------------------------------- */

export async function proposeEdit(
  blockId: string,
  blockCode: string,
  changes: BlockEditChanges,
  note?: string
): Promise<void> {
  if (Object.keys(changes).length === 0) {
    throw new Error('바뀐 값이 없습니다.');
  }

  if (isDevPreview()) {
    const id = `dev-req-${Date.now().toString(36)}`;
    DEV_REQUESTS.set(id, {
      id,
      blockId,
      blockCode,
      status: 'pending',
      changes,
      previous: null,
      note: note ?? null,
      reason: null,
      proposedBy: devPreviewUser()?.email ?? 'dev@localhost',
      proposedAt: new Date().toISOString(),
      reviewedBy: null,
      reviewedAt: null,
    });
    return;
  }

  const client = sb();
  const { error } = await client.from('cad_block_edit_request').insert({
    block_id: blockId,
    block_code: blockCode,
    changes,
    note: note ?? null,
  });
  fail('수정 제안 등록', error);

  await logActivity('block.edit_proposed', { payload: { blockCode, changes } });
}

/**
 * 승인/반려 처리.
 *
 * ★ 본인이 낸 제안은 본인이 처리할 수 없다 — DB(0009 RLS + RPC)와 화면 양쪽에서 막는다.
 *   화면 검증만 있으면 API 를 직접 불러 우회할 수 있고, DB 검증만 있으면
 *   사용자가 "왜 안 되는지" 이유를 못 본다.
 */
export async function reviewEdit(
  requestId: string,
  action: 'approve' | 'reject',
  reason?: string
): Promise<void> {
  if (action === 'reject' && !reason?.trim()) {
    throw new Error('반려에는 사유가 필요합니다.');
  }

  if (isDevPreview()) {
    const req = DEV_REQUESTS.get(requestId);
    if (!req) throw new Error('제안을 찾을 수 없습니다.');
    if (req.status !== 'pending') throw new Error('이미 처리된 제안입니다.');
    req.status = action === 'approve' ? 'approved' : 'rejected';
    req.reason = action === 'reject' ? (reason ?? null) : null;
    req.reviewedBy = devPreviewUser()?.email ?? 'dev@localhost';
    req.reviewedAt = new Date().toISOString();
    return;
  }

  const client = sb();

  if (action === 'approve') {
    const { error } = await client.rpc('cad_apply_block_edit', { p_request_id: requestId });
    fail('수정 승인', error);
  } else {
    const { error, count } = await client
      .from('cad_block_edit_request')
      .update({ status: 'rejected', reason }, { count: 'exact' })
      .eq('id', requestId)
      .eq('status', 'pending');
    fail('수정 반려', error);
    if (count === 0) {
      throw new Error('그 사이 다른 사람이 처리했습니다. 새로고침 후 다시 시도하세요.');
    }
  }

  await logActivity(`block.edit_${action === 'approve' ? 'approved' : 'rejected'}`, {
    payload: { requestId, reason },
  });
}

/* -------------------------------------------------------------------------- *
 * 개발 미리보기 — TS 카탈로그를 읽기 전용으로 흉내낸다
 * -------------------------------------------------------------------------- */

const DEV_REQUESTS = new Map<string, BlockEditRequestRow>();

function devBlocks(): BlockMasterRow[] {
  return STANDARD_BLOCKS.map((b: CatalogBlock, i) => {
    const params = b.spec ? normalizeParams(b.spec, {}) : {};
    const shelf = b.spec ? shelfLengthMeters(b.spec, params) : 0;
    return {
      id: `dev-${i}-${b.code}`,
      blockCode: b.code,
      name: b.name,
      categoryCode: b.categoryCode,
      defaultLayer: b.defaultLayer,
      widthMm: (params.width as number | undefined) ?? null,
      depthMm: (params.depth as number | undefined) ?? null,
      heightMm: null,
      shelfLengthPerUnitM: shelf > 0 ? shelf : null,
      clearance: b.clearance ?? {},
      utility: b.utility ?? {},
      cost: b.cost ?? {},
      status: 'approved' as const,
      staticEntities: null,
      resizable: false,
    };
  });
}
